import { api } from './http'
import { CODE } from '@/constants/errorCodes'
import type { BankStatus, OnboardingProgress, PortalMode, CustomerType } from '@/constants/dicts'
import type { AccountOpening, ClientType, Mt5Status } from './auth'

export interface ProfileResult {
  // 不含统一账号：它仅后台显示，后端 2026-09-15 起不再下发
  email: string
  phone: string | null
  /** 法定姓名，不可自助修改（改名走客服 → 客户修改提案） */
  fullName: string
  residenceCountry: string | null
  residentialAddress: string | null
  /**
   * **通知语言**（我们发给客户的邮件用哪种语言，cfd-notice 按它选模板）。接口 7.0 起可改（`updateLocale`），缺省 en-AU。
   * ⚠️ 它**不影响**接口返回的展示文案 —— 那些按请求头 `Accept-Language` 渲染（7.0 起）。
   * 所以界面语言切换（LangSwitch）**不调**写接口，两件事解耦；只有客户显式改「通知语言」才调。
   */
  locale: string
  onboardingProgress: OnboardingProgress
  portalMode: PortalMode
  /**
   * 客户类型（接口 0.8.0；二期 P01 起四值 RETAIL / SOPH_IND / WHOLESALE / SOPH_CORP）。公司户＝WHOLESALE / SOPH_CORP；
   * 免测门禁以 `QuizStatus.exempt` 为准、分流以 clientType 为准，这里只作展示 / 零售保护判断 / 兜底
   */
  customerType: CustomerType
  /** 证件有效期（Sumsub 回写）——到期前 60 天提醒（§14.2） */
  docExpiry: string | null
  /** 建档时间 = 邮箱验证时点（§14.2 统一账号行） */
  accountOpenedTime: string | null
  /** 登录密码上次修改时间（UTC；接口 4.0 新增）——安全区「登录密码」行常显 */
  passwordUpdatedTime: string | null
  /**
   * 登录密码剩余有效天数（接口 4.0 新增）。
   * 放在这里的好处是**刷新页面也拿得到**，不必依赖登录那一次响应。
   * ≤0 = 已过期，资料页显示「已过期，需立即修改」。
   */
  passwordExpireInDays: number | null
  /** 首户开立状态（接口 0.8.4）：PROVISIONING / FAILED / null，见 LoginResult.accountOpening */
  accountOpening?: AccountOpening | null
  /**
   * 客户类型小写形态（接口 0.10.0，与登录响应同源）。刷新页面时 session 没了，
   * 整套分流（菜单 / 任务弹窗 / 公司页）只能从这里拿——不放在这里，刷新一次公司户就变回零售形态。
   */
  clientType?: ClientType | null
  /** MT5 开通状态（接口 0.10.0）。前端优先于 accountOpening */
  mt5Status?: Mt5Status | null
  /**
   * 首登强制改密（接口 0.10.0）。本接口是 40301 闸门下仍放行的三个入口之一，
   * 强制改密页据此显示「首次登录，请设置您的登录密码」而不是「密码已过期」。
   */
  mustChangePassword?: boolean

  /* ---------- 原型 v-profile 那四行（接口 2026-09-10 补齐） ---------- */

  /** 出生日期（只读；姓名 / 生日 / 证件号无自助修改入口，§14.2） */
  dateOfBirth?: string | null
  /**
   * 税务居民身份。由开户申请第 3 步那五列推导，**不回税号本体**。
   * `country` 为 null = 客户没答过这一节（草稿期 / 老数据）——后端不缺省成 AU，前端也别猜。
   * `tinProvided` 对澳洲税务居民通常是 false：澳洲 TFN 我们不采集（境内开户不强制、属高敏信息），
   * 所以这一行的文案是「澳大利亚」/「新加坡 · TIN 已提供」，不是原型那句「TFN 已提供」。
   */
  taxResidency?: { country: string | null; tinProvided: boolean } | null
  /**
   * AML 筛查（客户级）。null=无结论 → **整行不显示**，不要用「—」占位（那看起来像「没通过」）。
   * 公司户授权代表本人那条不在这里，走 `activation/kyc/people` 的 `people[self].docs.aml`。
   * 0.11.9（二期 §14.6）起口径：最近一次认证的 AML 分项无命中即 PASSED；未筛查 / 命中 / 待审一律 null。
   */
  amlStatus?: 'PASSED' | null
  /**
   * 地址证明（客户级，0.11.9，二期 §14.7）：Sumsub 新 level 第三步的分项结论 GREEN → PASSED；
   * 老 level 通过的存量客户没有这一项 → null，资料页显示「未验证」（此前拿证件有效期当地址证明已验证，是错的）。
   */
  poaStatus?: 'PASSED' | null
  /** 最近登录时间（UTC）。一期只做「时间 + IP」，无城市 / 浏览器 */
  lastLoginTime?: string | null
  /** 最近登录 IP（原始地址，不做归属地解析） */
  lastLoginIp?: string | null
  /**
   * 身份证明的证件类型（零售）：Sumsub 原值，取客户级最近一条 PASSED case 的 `kyc_case.doc_type`。
   * 后端 2026-09-14 的答复**定稿并认做**（待排期 · 小），`接口9.0.yaml` 尚未收录，故可选。
   * 公司户仍用 `people[self].docs.idType`；没带回来时资料页退回到期提醒里的 `ComplianceAlert.docType`。
   */
  docType?: 'PASSPORT' | 'DRIVERS' | 'ID_CARD' | null
  /**
   * 须补填手机号（cfd-platform 决策 029，用户中心 spec 0.13.10）：CRM「恢复登录」时客户的手机号已被别人占用、客服选了清空，
   * 后端对**个人客户、已提交开户申请、主档手机号为空**给 true，其余 false。为 true 时全局布局弹不可关闭的补填弹窗（D-98）。
   * 老后端不带这个字段＝false。
   */
  phoneRequired?: boolean
}

/**
 * 补填手机号（决策 029）：区号 `+` 加 1~4 位数字，国内号 4~20 位数字（可带空格 / 连字符，后端去掉、并去掉开头的 0）。
 * 成功后再取 `/profile/me` 就是 `phoneRequired=false`、`phone` 为 E.164。
 */
export interface PhoneFillRequest {
  phoneCode: string
  phone: string
}

/**
 * 补填手机号的失败里由弹窗就地红字显示后端 message、不弹 toast 的三个码：
 * 409 号码已被其他客户使用（「该手机号已注册」）、40911 手机号本来就不为空（「如需修改请联系客服」）、422 格式不对。
 */
export const PHONE_FILL_INLINE_CODES: readonly number[] = [CODE.CONFLICT, CODE.STATE_CONFLICT, CODE.VALIDATION]

export interface BankAccountView {
  id: number
  bankName: string
  /** 展示形态 xxx-xxx */
  bsbDisplay: string
  /** 账号尾 4 位 —— 完整账号不出服务 */
  accountTail: string
  currency: string
  holderName: string
  status: BankStatus
  /**
   * 预置行（接口 0.10.0）：公司户开户时由代录一并登记的那一条，**开户即已审核**。
   * 前台标「开户时登记」并且**不给编辑/删除入口**——它是开户档案的一部分，改动要走客服。
   */
  preset?: boolean
  /*
   * 下面四个字段：后端 2026-09-14 的答复**定稿并认做**（待排期 · 中），`接口9.0.yaml` 尚未收录，
   * 全部可选、没带回来时对应小字不显示。库里 `bank_account.swift / bank_country / create_time` 一直有；
   * 提案号要反查 `cm_proposal`（BANK_CARD && target_ref=id 的最新一条）。mock 照定稿给出。
   */
  /** SWIFT / BIC 原值直出（公司户预置行常有；零售自助添加时可空） */
  swift?: string | null
  /** 银行所在国（ISO alpha-2）原值直出；AU 不必展示 */
  bankCountry?: string | null
  /** 客户修改提案号（CM-…）。**仅 `status=PENDING_REVIEW` 时下发**，其余 null（需求 §12 审核中账号要显示） */
  proposalNo?: string | null
  /** 提交日期 = `bank_account.create_time`（ISO） */
  submittedTime?: string | null
  /** 银行地址（2026-09-23 UAT CP-015 起添加时必填）；此前添加的存量账户为 null */
  bankAddress?: string | null
}

export interface BankAccountAddRequest {
  bankName: string
  bankCountry?: string
  /** 银行地址，**必填**（境外电汇需要收款行地址；空白 422），限长 255 */
  bankAddress: string
  /** 须与开户法定姓名一致，第三方账户一律拒绝（422） */
  holderName: string
  bsb: string
  accountNo: string
  swift?: string
  currency: string
  /**
   * 近 12 个月银行对账单附件引用，**必传**。
   * 先 `POST /c/files` 且 **category=`CM_DOC`**（4.0 已确认这个类别是对的）取 fileId。
   *
   * ⚠️ 4.0 起后端**真的会校验并挂接**它（linkType=BANK_ACCOUNT）：
   * 非本人 / 类别不对 / 已挂到别的单据 → **42215**。
   * 2.0 时它只校验非空不挂接，于是上传满 24h 被回收器清掉、绑卡却仍"提交成功" ——
   * 那种"看起来有、其实没了"正是不缓存 fileId 的原因。
   */
  statementRef: string
}

export const profileApi = {
  me: () => api.get<ProfileResult>('/c/profile/me'),
  bankAccounts: () => api.get<BankAccountView[]>('/c/profile/bank-accounts'),
  /** 已有一张审核中账号时禁止再提交（409） */
  addBankAccount: (body: BankAccountAddRequest) =>
    api.post<BankAccountView>('/c/profile/bank-accounts', body),

  /**
   * 修改通知语言（接口 7.0 新增，能力标签 `preference`，**全模式常开** —— 被拒户、冷却户照样收邮件）。
   * 取值 zh-CN / zh-HK / en-AU（繁体 2026-10-12 加）；不认识的返回 422（不静默回落）。返回归一后实际落库的值。
   * 同值重复提交幂等。
   */
  updateLocale: (locale: 'zh-CN' | 'zh-HK' | 'en-AU') => api.post<string>('/c/profile/locale', { locale }),

  /**
   * 补填手机号（决策 029，`PUT /c/profile/phone`）。能力标签与通知语言相同（`preference`，全模式可用、含受限模式）。
   * 成功 `data: null`；{@link PHONE_FILL_INLINE_CODES} 三个码不弹 toast，由弹窗就地显示，其余走全局处理。
   */
  fillPhone: (body: PhoneFillRequest) =>
    api.put<null>('/c/profile/phone', body, { inline: PHONE_FILL_INLINE_CODES }),
}

/** 持有人须与开户法定姓名一致（反洗钱政策）。比对前归一化空白与大小写 */
export function holderMatches(holder: string, legalName: string): boolean {
  const norm = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase()
  return norm(holder) === norm(legalName)
}

/** BSB：6 位数字，可带连字符；展示为 xxx-xxx */
export function normalizeBsb(v: string): string {
  return v.replace(/\D/g, '').slice(0, 6)
}
export function formatBsb(v: string): string {
  const d = normalizeBsb(v)
  return d.length > 3 ? `${d.slice(0, 3)}-${d.slice(3)}` : d
}
